import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  User, 
  CreditCard, 
  Activity, 
  Users, 
  Shield,
  MessageSquare,
  BarChart3,
  Settings,
  Zap,
  Clock
} from 'lucide-react';
import { UserData } from '@/services/admin/user-management-service';
import { formatDistanceToNow, format } from 'date-fns';
import { EditUserDialog } from './EditUserDialog';
import { UserOverviewTab } from './tabs/UserOverviewTab';
import { UserTradingSetupTab } from './tabs/UserTradingSetupTab';
import { UserBotsPerformanceTab } from './tabs/UserBotsPerformanceTab';
import { UserAIFeaturesTab } from './tabs/UserAIFeaturesTab';
import { UserSubscriptionBillingTab } from './tabs/UserSubscriptionBillingTab';
import { UserSecurityAuditTab } from './tabs/UserSecurityAuditTab';

interface UserDetailModalProps {
  user: UserData | null;
  isOpen: boolean;
  onClose: () => void;
  onEditUser: (user: UserData) => void;
  onToggleUserStatus: (user: UserData) => void;
  onDeleteUser: (user: UserData) => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  user,
  isOpen,
  onClose,
  onEditUser,
  onToggleUserStatus,
  onDeleteUser,
}) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleEditUser = () => {
    setIsEditDialogOpen(true);
  };

  const handleUserUpdated = () => {
    // Call the parent's onEditUser to refresh data
    if (user) {
      onEditUser(user);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!w-[95vw] lg:!w-[66vw] !max-w-none max-h-[95vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {user.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-semibold">{user.name}</div>
              <div className="text-sm text-muted-foreground font-normal">{user.email}</div>
            </div>
            {user.isAdmin && (
              <Badge variant="destructive" className="ml-auto">
                <Shield className="h-3 w-3 mr-1" />
                Admin
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview" className="flex-1 overflow-hidden">
          <TabsList className="grid w-full grid-cols-6 h-12">
            <TabsTrigger value="overview" className="flex items-center gap-2 text-xs">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="trading" className="flex items-center gap-2 text-xs">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Trading</span>
            </TabsTrigger>
            <TabsTrigger value="bots" className="flex items-center gap-2 text-xs">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Bots</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2 text-xs">
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">AI</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2 text-xs">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Billing</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2 text-xs">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
          </TabsList>
          
          <div className="custom-scrollbar scrollbar-thin scrollbar-thumb-border/40 scrollbar-track-transparent hover:scrollbar-thumb-border/60 overflow-y-auto max-h-[75vh] mt-6 pr-2">
            <TabsContent value="overview" className="space-y-4">
              <UserOverviewTab
                user={user}
                onEditUser={handleEditUser}
                onToggleUserStatus={onToggleUserStatus}
                onDeleteUser={onDeleteUser}
              />
            </TabsContent>

            <TabsContent value="trading" className="space-y-4">
              <UserTradingSetupTab userId={user.id} />
            </TabsContent>

            <TabsContent value="bots" className="space-y-4">
              <UserBotsPerformanceTab userId={user.id} />
            </TabsContent>

            <TabsContent value="ai" className="space-y-4">
              <UserAIFeaturesTab userId={user.id} />
            </TabsContent>

            <TabsContent value="billing" className="space-y-4">
              <UserSubscriptionBillingTab userId={user.id} />
            </TabsContent>

            <TabsContent value="security" className="space-y-4">
              <UserSecurityAuditTab userId={user.id} user={user} />
            </TabsContent>
          </div>
        </Tabs>

        <EditUserDialog
          user={user}
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          onUserUpdated={handleUserUpdated}
        />
      </DialogContent>
    </Dialog>
  );
};